// Ported from microsites/womens-conference/src/components/ContactCard.tsx.
// Changes: the server action is the event API's existing contact route (which
// writes to the event's primary contact, so the card names that person);
// next/image -> img; faces arrive as URLs.
import { useActionState, useState } from 'react';
import { askQuestion } from '@/lib/api';
import { art } from '../lib/art';
import { Avatar } from './Avatar';
import { Letter } from './Botanical';
import { Sheet, SheetBand } from './Sheet';
import type { Copy } from '../lib/copy';

export interface ContactResult {
  ok: boolean;
  message: string;
}

export interface HostContact {
  name: string;
  role: string;
  imageUrl: string | null;
}

export interface HouseholdMember {
  contactId: number;
  name: string;
  firstName: string;
  email: string | null;
  imageUrl: string | null;
}

/** The microsite's sendHostNote, as a client action over POST /contact. */
export function hostNoteAction(apiBaseUrl: string, eventId: number, pretend: boolean) {
  return async (_prev: ContactResult | null, form: FormData): Promise<ContactResult> => {
    const text = (k: string) => String(form.get(k) ?? '').trim();
    if (pretend) return { ok: true, message: 'Not sent: you are in the time machine.' };
    try {
      await askQuestion(apiBaseUrl, eventId, {
        name: text('name'),
        email: text('email'),
        message: text('message'),
        contactId: Number(text('contactId')) || undefined,
        church: text('church') || undefined,
      });
      return { ok: true, message: 'Sent. The team reads these.' };
    } catch (err) {
      return {
        ok: false,
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Could not send it. Try again in a minute.',
      };
    }
  };
}

/**
 * Questions, allergies, "can I bring my mom", and the woman who cannot find
 * her confirmation email.
 *
 * The invitation is the ministry's ("Let us know") and the face under it is
 * the person who actually answers, so it reads as a team you can reach
 * rather than one woman's inbox you are interrupting. The note is relayed
 * server-side, so her address is never printed on a public page for a
 * scraper to harvest.
 *
 * It is the one painted card on the page: the masthead's watercolor ground,
 * same as the footer it sits above, so the page closes on the colour it
 * opened with instead of on one more sheet of paper.
 *
 * If we know who is reading, the form is already filled in. MP knows her
 * name, her email, and everyone else at her kitchen table, so "who's asking"
 * is a row of faces to tap rather than two fields to type. Someone else
 * entirely (a friend she forwarded the link to) still gets the plain fields.
 */
export function ContactCard({
  host,
  household,
  action,
  copy,
}: {
  host: HostContact;
  copy: Copy;
  /** The signed-in attendee's household, her first. Empty for guests. */
  household: HouseholdMember[];
  action: (prev: ContactResult | null, formData: FormData) => Promise<ContactResult>;
}) {
  const [open, setOpen] = useState(false);
  const [state, formAction, pending] = useActionState(action, null);
  // Whose question this is: a household Contact_ID, or 'other' for a person
  // MP has never met. Seeded to her, since it usually is.
  const [who, setWho] = useState<number | 'other'>(household[0]?.contactId ?? 'other');
  const selected = household.find((m) => m.contactId === who);
  // "Woodside Women · Woodside Women's Ministry" says the name twice. When the
  // role already carries the name, the role alone is the line.
  const hostLine = host.role.includes(host.name) ? host.role : `${host.name} · ${host.role}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="wm-lift group text-wm-cream relative flex h-full w-full items-center gap-5 overflow-hidden rounded-[var(--radius-wm-card)] p-5 text-left sm:p-6"
      >
        <img
          src={art('/art/watercolor.png')}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Darkened for cream type, the same amount the masthead uses. */}
        <span aria-hidden className="bg-wm-sage-950/45 absolute inset-0" />
        {/* Her face when MP has one; otherwise a drawn note, which says
            "write to us" where a monogram in a disc said nothing. */}
        {host.imageUrl ? (
          <Avatar
            name={host.name}
            imageSrc={host.imageUrl}
            size={64}
            className="ring-wm-cream/25 relative shrink-0 ring-2"
          />
        ) : (
          <span aria-hidden className="relative flex w-14 shrink-0 justify-center">
            <Letter className="text-wm-cream h-12 w-auto -rotate-[6deg] transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:rotate-0" />
          </span>
        )}
        <span className="relative min-w-0 grow">
          <span className="wm-kicker wm-kicker-on-dark block">{copy['contact.eyebrow']}</span>
          <span className="wm-display mt-0.5 block text-3xl">{copy['contact.heading']}</span>
          <span className="text-wm-sage-200 mt-1 block text-sm">{hostLine}</span>
        </span>
        <span
          aria-hidden
          className="wm-glass-dark text-wm-cream/80 group-hover:text-wm-cream relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors"
        >
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8h10M9 4l4 4-4 4" />
          </svg>
        </span>
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={copy['contact.heading']}
        band={
          <SheetBand
            kicker={copy['contact.eyebrow']}
            title={copy['contact.heading']}
            subtitle={hostLine}
            lead={
              host.imageUrl ? (
                <Avatar
                  name={host.name}
                  imageSrc={host.imageUrl}
                  size={56}
                  className="ring-wm-cream/25 ring-2"
                />
              ) : (
                <Letter className="text-wm-cream h-11 w-auto -rotate-[6deg]" />
              )
            }
          />
        }
      >
        <div className="flex flex-col gap-6 p-6 sm:px-7">
          <p className="text-wm-sage-700 text-sm">{copy['contact.blurb']}</p>

          <form action={formAction} className="flex flex-col gap-4">
            {/* Honeypot. Hidden from people, filled by bots, checked server-side. */}
            <input
              type="text"
              name="church"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
            />

            {household.length > 0 ? (
              <fieldset>
                <legend className="wm-kicker mb-2">Who's asking</legend>
                <div className="flex flex-wrap gap-2">
                  {household.map((member) => {
                    const mine = who === member.contactId;
                    return (
                      <button
                        key={member.contactId}
                        type="button"
                        onClick={() => setWho(member.contactId)}
                        aria-pressed={mine}
                        className={`flex items-center gap-2 rounded-full border py-1.5 pr-4 pl-1.5 text-sm transition-colors ${
                          mine
                            ? 'border-wm-sage-800 bg-wm-sage-100 font-semibold'
                            : 'border-wm-sage-200 bg-wm-petal hover:bg-wm-cream'
                        }`}
                      >
                        <Avatar name={member.name} imageSrc={member.imageUrl} size={28} />
                        {member.firstName}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setWho('other')}
                    aria-pressed={who === 'other'}
                    className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                      who === 'other'
                        ? 'border-wm-sage-800 bg-wm-sage-100 font-semibold'
                        : 'border-wm-sage-200 bg-wm-petal hover:bg-wm-cream'
                    }`}
                  >
                    Someone else
                  </button>
                </div>
              </fieldset>
            ) : null}

            {selected ? (
              // Her name is settled by the chip she tapped; only the address
              // stays editable, because the one on file is the one thing that
              // is often stale.
              <>
                <input type="hidden" name="name" value={selected.name} />
                <input type="hidden" name="contactId" value={selected.contactId} />
                <label className="block">
                  <span className="wm-kicker">Email, so we can reply</span>
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={200}
                    defaultValue={selected.email ?? ''}
                    // Remount per person so switching chips actually swaps
                    // the address instead of keeping the first one typed.
                    key={selected.contactId}
                    className="border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none"
                  />
                </label>
              </>
            ) : (
              <>
                <label className="block">
                  <span className="wm-kicker">Your name</span>
                  <input
                    name="name"
                    required
                    maxLength={100}
                    autoComplete="name"
                    className="border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none"
                  />
                </label>
                <label className="block">
                  <span className="wm-kicker">Email, so we can reply</span>
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={200}
                    autoComplete="email"
                    className="border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none"
                  />
                </label>
              </>
            )}

            <label className="block">
              <span className="wm-kicker">Your question</span>
              <textarea
                name="message"
                required
                rows={5}
                maxLength={4000}
                className="border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none"
              />
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={pending || state?.ok}
                className="wm-lift bg-wm-marigold text-wm-sage-950 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
              >
                {pending ? 'Sending…' : state?.ok ? 'Sent' : 'Send it'}
              </button>
              {state ? (
                <span
                  role="status"
                  className={`text-xs ${state.ok ? 'text-wm-sage-500' : 'text-wm-plum-deep'}`}
                >
                  {state.message}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </Sheet>
    </>
  );
}
